import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  getRecommendedWorkflows,
  createConversation,
  runWorkflow,
  deleteConversation,
} from '@/lib/api';
import { getWorkflowDisplayName } from '@/lib/workflow-metadata';

interface WorkflowRecommendationBannerProps {
  codebaseId: string;
}

export function WorkflowRecommendationBanner({
  codebaseId,
}: WorkflowRecommendationBannerProps): React.ReactElement | null {
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['recommended-workflows', codebaseId],
    queryFn: () => getRecommendedWorkflows(codebaseId),
  });

  if (isLoading || !data) return null;

  const { recommendation, mode } = data;

  // Not consumed yet — nudge toward the Projects tab.
  if (mode === null || recommendation === null) {
    return (
      <div className="rounded-xl border border-border/70 bg-surface-elevated/60 px-4 py-3 text-sm text-text-secondary">
        This project isn&apos;t consumed yet.{' '}
        <Link to="/projects" className="text-primary hover:underline">
          Consume it on the Projects tab
        </Link>{' '}
        to get phase-aware workflow recommendations.
      </div>
    );
  }

  const next = recommendation.recommended[0];

  async function runNext(workflowName: string): Promise<void> {
    if (running) return;
    setRunning(true);
    setError(null);
    let conversationId: string | undefined;
    let started = false;
    try {
      ({ conversationId } = await createConversation(codebaseId));
      await runWorkflow(
        workflowName,
        conversationId,
        `Run the ${recommendation?.phaseName ?? ''} phase workflow for this project.`
      );
      started = true;
      navigate(`/chat/${conversationId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start workflow');
      if (conversationId !== undefined && !started) {
        void deleteConversation(conversationId).catch((cleanupErr: unknown) => {
          console.warn('[WorkflowRecommendationBanner] Failed to clean up orphan conversation', {
            conversationId,
            error: cleanupErr,
          });
        });
      }
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="font-medium text-text-primary">
              Phase {recommendation.phase}: {recommendation.phaseName}
            </span>
            <Badge variant="secondary" className="capitalize">
              {mode}
            </Badge>
          </div>
          <p className="text-sm text-text-secondary">{recommendation.rationale}</p>
          {recommendation.recommended.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {recommendation.recommended.map(wf => (
                <Badge key={wf} variant="outline" className="text-[11px]">
                  {getWorkflowDisplayName(wf)}
                </Badge>
              ))}
            </div>
          )}
          {error && <p className="text-xs text-error">{error}</p>}
        </div>
        {next && (
          <Button
            onClick={() => {
              void runNext(next);
            }}
            disabled={running}
            className="shrink-0"
          >
            {running ? 'Starting…' : `Run ${getWorkflowDisplayName(next)}`}
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
